import { Transfer, CbmImpl } from "~/modules/cbm";
import { Action } from "~/modules/actions/Action";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import {
    checkChannelSid,
    checkFlexInstanceSid,
    checkInteractionSid,
    checkReservationExists,
    getReservationsByTask
} from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { AccountConfigDataContainer } from "~/modules/config/AccountConfig/AccountConfigImpl/AccountConfigDataContainer/AccountConfigDataContainer";
import { ChannelNoteServiceImpl } from "~/backend/generated/Notes/api/channelNote.service";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";




export interface GetConversationTransfersOptions {
    withNotes?: boolean;
}




export interface ConversationTransfer {
    sid: string;
    channelSid: string;
    type: string;
    from: string;
    to: string;
    status: string;
    reason: string;
    dateCreated: Date;
    summary?: string;
}



































export class GetConversationTransfers implements Action<Promise<Array<Transfer | ConversationTransfer>>> {
    


    readonly #taskSid: string;

    


    readonly #options?: GetConversationTransfersOptions;

    constructor(taskSid: string, options?: GetConversationTransfersOptions) {
        this.#taskSid = taskSid;
        this.#options = options;
    }

    async run(ctx: any): Promise<ConversationTransfer[]> {
        const context = ctx as ContextManager;
        const logger = getLogger(context)(LoggerName.Actions);

        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const worker = taskRouter.worker;

        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        const notesService = context.retrieveInstanceOf(
            ContextInstance.ChannelNotesServiceImpl,
            ChannelNoteServiceImpl
        );

        const accountConfigDataContainer = context.retrieveInstanceOf(
            ContextInstance.AccountConfigDataContainer,
            AccountConfigDataContainer
        );

        logger.debug(`GetConversationTransfers invoked with taskSid: ${this.#taskSid}`);

        const reservation = getReservationsByTask(worker, this.#taskSid)[0];
        checkReservationExists(reservation, this.#taskSid, {
            actionName: "GetConversationTransfers",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.GetConversationTransfersCompleted,
            extra: {
                taskSid: this.#taskSid
            },
            logger
        });

        const task = reservation.task;

        const interactionSid = task.attributes.flexInteractionSid as string | undefined;

        checkInteractionSid(interactionSid, this.#taskSid, {
            actionName: "GetConversationTransfers",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.GetConversationTransfersCompleted,
            extra: {
                taskSid: this.#taskSid
            },
            logger
        });

        const channelSid = task.attributes.flexInteractionChannelSid as string | undefined;

        checkChannelSid(channelSid, this.#taskSid, {
            actionName: "GetConversationTransfers",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.GetConversationTransfersCompleted,
            extra: {
                taskSid: this.#taskSid
            },
            logger
        });

        let instanceSid = accountConfigDataContainer.get().flexInstanceSid;
        instanceSid = checkFlexInstanceSid(instanceSid, {
            actionName: "GetConversationTransfers",
            moduleName: "actions/Conversations",
            worker,
            analytics,
            context,
            event: EVENTS.GetConversationTransfersCompleted,
            extra: { taskSid: this.#taskSid },
            logger
        });
        try {
            const transfers = await cbm.getChannelTransfers(instanceSid, interactionSid!, channelSid!);

            if (!transfers) {
                sendWorkerTrackingEvent(EVENTS.GetConversationTransfersCompleted, analytics, worker, {
                    taskSid: this.#taskSid,
                    withNotes: this.#options?.withNotes
                });

                return Promise.resolve([]);
            }
            const transfersWithNotes: Array<ConversationTransfer> = await Promise.all(
                transfers.map(async (transfer) => {
                    let summary = "";
                    let to = transfer.to;

                    if (this.#options?.withNotes) {
                        try {
                            if (transfer.noteSid) {
                                const note = await notesService.getChannelNote(instanceSid, transfer.noteSid);
                                summary = note?.summary ?? "";
                            }
                        } catch (error) {
                            const errorMsg = `GetConversationTransfers: Failed to fetch notes for transfer for task ${
                                this.#taskSid
                            }`;
                            logger.error(errorMsg, error);
                        }
                    }
                    if (transfer.to.startsWith("WK")) {
                        try {
                            const workerInfo = await taskRouter.workspace.fetchWorker(transfer.to);
                            to = (workerInfo.attributes?.full_name as string | undefined) ?? "";
                        } catch (error) {
                            const errorMsg = `GetConversationTransfers: Failed to fetch worker ${
                                transfer.to
                            } for task ${this.#taskSid}`;
                            logger.error(errorMsg, error);
                        }
                    }

                    if (transfer.to.startsWith("WQ")) {
                        try {
                            const queue = await taskRouter.workspace.fetchTaskQueue(transfer.to);
                            to = queue.name ?? "";
                        } catch (error) {
                            const errorMsg = `GetConversationTransfers: Failed to fetch queue ${transfer.to} for task ${
                                this.#taskSid
                            }`;
                            logger.error(errorMsg, error);
                        }
                    }

                    return {
                        sid: transfer.sid,
                        channelSid: transfer.channelSid,
                        type: transfer.type,
                        from: transfer.from,
                        to,
                        status: transfer.status,
                        reason: transfer.reason,
                        dateCreated: new Date(transfer.dateCreated),
                        summary
                    };
                })
            );

            sendWorkerTrackingEvent(EVENTS.GetConversationTransfersCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                withNotes: this.#options?.withNotes
            });

            const sortedTransfers = transfersWithNotes.toSorted(
                (a, b) => a.dateCreated.getTime() - b.dateCreated.getTime()
            );
            return Promise.resolve(sortedTransfers);
        } catch (error) {
            const errorMessage = toCauseMessage(error);
            if (errorMessage?.toLowerCase().includes("forbidden")) {
                const errorMsg = `GetConversationTransfers: Failed to get conversation transfer for task ${
                    this.#taskSid
                }, interactionSid ${interactionSid!}, channelSid ${channelSid!}. Conversation transfer might not be enabled in the configuration`;
                logger.error(errorMsg);
                sendWorkerTrackingEvent(EVENTS.GetConversationTransfersCompleted, analytics, worker, {
                    error: errorMsg,
                    taskSid: this.#taskSid,
                    withNotes: this.#options?.withNotes
                });
                return Promise.reject(
                    createFlexSdkError(context)(
                        ErrorCode.ConversationTransferRetrievingForbidden,
                        { severity: ErrorSeverity.Error },
                        errorMsg,
                        error
                    )
                );
            }

            const errorMsg = `GetConversationTransfers: Failed to get conversation transfer for task ${
                this.#taskSid
            }, interactionSid ${interactionSid!}, channelSid ${channelSid!}`;
            logger.error(errorMsg, error);
            sendWorkerTrackingEvent(EVENTS.GetConversationTransfersCompleted, analytics, worker, {
                error: `${errorMsg} (${toCauseMessage(error)})`,
                taskSid: this.#taskSid,
                withNotes: this.#options?.withNotes
            });

            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToRetrieveConversationTransfer,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }
}
